/**
 * 依背景圖亮度自動決定文字要用黑或白。
 *
 * 用法：傳入「畫著背景圖的那個元素」與若干個應用座標區域，
 * 回傳每個區域建議的文字顏色。結果會快取，同一張圖同一塊區域只算一次。
 *
 *   const tone = await pickTextColors(el, {
 *     slogan: { x: 80, y: 200, w: 600, h: 300 }
 *   })
 *   // -> { slogan: 'white' }
 *
 * 注意：區域用的是「應用座標」—— 也就是 #app 這塊固定 1920x1080 畫布的座標，
 * 不是視窗座標。用視窗座標會隨頁面捲動飄移，同一張圖在不同時間點取到不同像素。
 */

const imageCache = new Map(); // url -> Promise<HTMLImageElement>
const resultCache = new Map(); // cacheKey -> 'black' | 'white'

// 亮度門檻：超過視為亮底，用黑字
const DEFAULT_THRESHOLD = 150;

// 取樣間隔：每 4 個像素取 1 個，足夠準確又不會太慢
const SAMPLE_STRIDE = 16;

// 背景圖有透明區域時，透出來的是頁面底色（白），要合成後再算亮度
const BACKDROP = 255;

function loadImage(url) {
  if (!imageCache.has(url)) {
    imageCache.set(
      url,
      new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("load failed: " + url));
        img.src = url;
      })
    );
  }
  return imageCache.get(url);
}

/** 取出元素 background-image 的實際網址，沒有就回 null */
export function backgroundImageUrl(el) {
  const bg = window.getComputedStyle(el).backgroundImage;
  if (!bg || bg === "none") return null;
  return bg.replace(/^url\(["']?/, "").replace(/["']?\)$/, "");
}

/** 計算圖片某區域的平均亮度（0–255），取不到回 null */
function regionLuminance(img, drawRect, region) {
  // 應用座標 -> 圖片座標
  const sx = img.naturalWidth / drawRect.width;
  const sy = img.naturalHeight / drawRect.height;
  const ix = Math.round((region.x - drawRect.x) * sx);
  const iy = Math.round((region.y - drawRect.y) * sy);
  const iw = Math.max(1, Math.round(region.w * sx));
  const ih = Math.max(1, Math.round(region.h * sy));

  const canvas = document.createElement("canvas");
  canvas.width = iw;
  canvas.height = ih;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, ix, iy, iw, ih, 0, 0, iw, ih);

  let data;
  try {
    data = ctx.getImageData(0, 0, iw, ih).data;
  } catch (e) {
    // 跨網域圖片會污染 canvas，讀不到就放棄自動判斷
    return null;
  }

  let sum = 0;
  let count = 0;
  for (let i = 0; i < data.length; i += SAMPLE_STRIDE) {
    const a = data[i + 3] / 255;
    // 先合成到頁面底色，否則透明區域會被當成純黑
    const r = data[i] * a + BACKDROP * (1 - a);
    const g = data[i + 1] * a + BACKDROP * (1 - a);
    const b = data[i + 2] * a + BACKDROP * (1 - a);
    sum += 0.2126 * r + 0.7152 * g + 0.0722 * b;
    count++;
  }
  return count ? sum / count : null;
}

/**
 * @param {HTMLElement} el       畫著背景圖的元素
 * @param {Object} regions       { 名稱: { x, y, w, h } }，視窗座標
 * @param {number} [threshold]   亮度門檻，預設 150
 * @returns {Promise<Object>}    { 名稱: 'black' | 'white' }
 */
export async function pickTextColors(el, regions, threshold = DEFAULT_THRESHOLD) {
  const out = {};
  const keys = Object.keys(regions);

  const url = el && backgroundImageUrl(el);
  if (!url) {
    // 沒有背景圖就沿用原本的黑字
    keys.forEach(k => (out[k] = "black"));
    return out;
  }

  // 換算成以 #app 為原點的座標，避免受捲動影響
  const container = el.closest("#app") || document.documentElement;
  const elRect = el.getBoundingClientRect();
  const boxRect = container.getBoundingClientRect();
  const rect = {
    x: elRect.left - boxRect.left,
    y: elRect.top - boxRect.top,
    width: elRect.width,
    height: elRect.height
  };

  let img;
  try {
    img = await loadImage(url);
  } catch (e) {
    keys.forEach(k => (out[k] = "black"));
    return out;
  }

  keys.forEach(name => {
    const r = regions[name];
    const cacheKey = [
      url,
      Math.round(rect.x),
      Math.round(rect.y),
      Math.round(rect.width),
      Math.round(rect.height),
      r.x,
      r.y,
      r.w,
      r.h,
      threshold
    ].join(":");

    if (resultCache.has(cacheKey)) {
      out[name] = resultCache.get(cacheKey);
      return;
    }

    const lum = regionLuminance(img, rect, r);
    const tone = lum == null ? "black" : lum > threshold ? "black" : "white";
    resultCache.set(cacheKey, tone);
    out[name] = tone;
  });

  return out;
}
